import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/GoogleDriveUpload.css';
import { supabase } from '../supabase';

// Constants
const SHARED_DRIVE_ID = '0ABmJf1TNbmOjUk9PVA';

// Export the uploadToGoogleDrive function for direct use in other components
export const uploadToGoogleDrive = async (selectedFiles, workspaceId) => {
  if (!selectedFiles || selectedFiles.length === 0) {
    throw new Error('No files selected');
  }

  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }

  const accessToken = await getAccessToken();
  console.log('Starting file upload with access token');

  // Get client information from the database
  const { data: clientData, error: clientError } = await supabase
    .from('clients')
    .select('id, name')
    .eq('id', workspaceId)
    .single();

  if (clientError) {
    console.error('Error fetching client data:', clientError);
    throw new Error(`Could not find client information: ${clientError.message}`);
  }

  if (!clientData) {
    throw new Error(`No client found with ID: ${workspaceId}`);
  }

  const clientName = clientData.name;
  const clientFolderName = `${clientName}-${workspaceId}`;
  console.log(`Client folder name: ${clientFolderName}`);

  // Check if the client folder already exists in the shared drive
  const clientFolderResponse = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=name='${encodeURIComponent(clientFolderName)}' and mimeType='application/vnd.google-apps.folder' and trashed=false&corpora=drive&driveId=${SHARED_DRIVE_ID}&includeItemsFromAllDrives=true&supportsAllDrives=true&fields=files(id,name,parents)`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      }
    }
  );

  if (!clientFolderResponse.ok) {
    const errorData = await clientFolderResponse.json();
    console.error('Failed to check for client folder:', errorData);
    throw new Error(`Could not check for client folder: ${JSON.stringify(errorData)}`);
  }

  const folderData = await clientFolderResponse.json();
  console.log('Folder search result:', folderData);

  let clientFolderId;
  
  if (folderData.files && folderData.files.length > 0) {
    // Folder exists, use it
    clientFolderId = folderData.files[0].id;
    console.log(`Found existing client folder: ${clientFolderName} with ID: ${clientFolderId}`);
  } else {
    // Create a new folder for the client in the shared drive
    const createFolderResponse = await fetch('https://www.googleapis.com/drive/v3/files', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: clientFolderName,
        mimeType: 'application/vnd.google-apps.folder',
        driveId: SHARED_DRIVE_ID,
        parents: [SHARED_DRIVE_ID],
        fields: 'id,name,parents'
      }),
    });

    if (!createFolderResponse.ok) {
      const errorData = await createFolderResponse.json();
      console.error('Failed to create client folder:', errorData);
      
      // Try alternative approach for creating folder in shared drive
      const altCreateFolderResponse = await fetch('https://www.googleapis.com/drive/v3/files?supportsAllDrives=true', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: clientFolderName,
          mimeType: 'application/vnd.google-apps.folder',
          parents: [SHARED_DRIVE_ID]
        }),
      });

      if (!altCreateFolderResponse.ok) {
        const altErrorData = await altCreateFolderResponse.json();
        console.error('Failed to create client folder (alternative method):', altErrorData);
        throw new Error(`Could not create client folder: ${JSON.stringify(altErrorData)}`);
      }

      const newFolder = await altCreateFolderResponse.json();
      clientFolderId = newFolder.id;
      console.log(`Created new client folder (alternative method): ${clientFolderName} with ID: ${clientFolderId}`);
    } else {
      const newFolder = await createFolderResponse.json();
      clientFolderId = newFolder.id;
      console.log(`Created new client folder: ${clientFolderName} with ID: ${clientFolderId}`);
    }
  }

  // Now upload each file to the client folder
  for (const fileObj of selectedFiles) {
    const file = fileObj.file;
    console.log(`Uploading file: ${file.name} to client folder: ${clientFolderName}`);
    
    // Create metadata with parents parameter to specify the client folder
    const metadata = {
      name: file.name,
      parents: [clientFolderId]
    };

    // Create form data
    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    // Upload parameters - always include supportsAllDrives for shared drives
    const uploadParams = 'uploadType=multipart&supportsAllDrives=true';
    
    // Upload file
    const response = await fetch(`https://www.googleapis.com/upload/drive/v3/files?${uploadParams}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      body: form
    });

    if (!response.ok) {
      let errorMsg;
      try {
        const errorData = await response.json();
        console.error('Upload failed:', errorData);
        errorMsg = JSON.stringify(errorData);
      } catch (e) {
        errorMsg = await response.text();
        console.error('Upload failed with text response:', errorMsg);
      }
      
      throw new Error(`Failed to upload file: ${response.status} ${response.statusText} - ${errorMsg}`);
    }

    const data = await response.json();
    console.log(`File uploaded successfully with ID: ${data.id} to client folder: ${clientFolderName}`);
  }

  return true;
};

const getAccessToken = async () => {
  try {
    const credentials = {
      type: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_TYPE,
      project_id: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_PROJECT_ID,
      private_key_id: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY_ID,
      private_key: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY,
      client_email: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_CLIENT_EMAIL,
      client_id: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_CLIENT_ID,
      auth_uri: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_AUTH_URI,
      token_uri: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_TOKEN_URI,
      auth_provider_x509_cert_url: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_AUTH_PROVIDER_X509_CERT_URL,
      client_x509_cert_url: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_CLIENT_X509_CERT_URL,
      universe_domain: process.env.REACT_APP_GOOGLE_SERVICE_ACCOUNT_UNIVERSE_DOMAIN
    };

    const jwtHeader = {
      alg: 'RS256',
      typ: 'JWT',
      kid: credentials.private_key_id
    };

    const now = Math.floor(Date.now() / 1000);
    const jwtClaim = {
      iss: credentials.client_email,
      scope: 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive',
      aud: 'https://oauth2.googleapis.com/token',
      exp: now + 3600,
      iat: now
    };

    const encodedHeader = btoa(JSON.stringify(jwtHeader)).replace(/=/g, '');
    const encodedClaim = btoa(JSON.stringify(jwtClaim)).replace(/=/g, '');

    const signatureInput = `${encodedHeader}.${encodedClaim}`;
    const signature = await crypto.subtle.sign(
      { name: 'RSASSA-PKCS1-v1_5', hash: { name: 'SHA-256' } },
      await crypto.subtle.importKey(
        'pkcs8',
        new Uint8Array(atob(credentials.private_key.replace(/\\n/g, '').split('-----')[2]).split('').map(c => c.charCodeAt(0))),
        { name: 'RSASSA-PKCS1-v1_5', hash: { name: 'SHA-256' } },
        false,
        ['sign']
      ),
      new TextEncoder().encode(signatureInput)
    );

    const encodedSignature = btoa(String.fromCharCode(...new Uint8Array(signature))).replace(/=/g, '');
    const jwt = `${signatureInput}.${encodedSignature}`;

    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
        assertion: jwt,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Token request failed:', errorData);
      throw new Error(`Failed to get access token: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Successfully obtained access token');
    return data.access_token;
  } catch (err) {
    console.error('Error in getAccessToken:', err);
    throw new Error(`Authentication failed: ${err.message}`);
  }
};

function GoogleDriveUpload({ selectedFiles, onUploadComplete, workspaceId }) {
  const { language } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setError(language === 'en' ? "No files selected!" : "Keine Dateien ausgewählt!");
      return;
    }

    if (!workspaceId) {
      setError(language === 'en' ? "No workspace selected!" : "Kein Arbeitsbereich ausgewählt!");
      return;
    }

    setUploading(true);
    setError('');
    setSuccessMessage('');

    try {
      await uploadToGoogleDrive(selectedFiles, workspaceId);
      
      setSuccessMessage(language === 'en' ? "Documents uploaded successfully!" : "Dokumente erfolgreich hochgeladen!");
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (err) {
      console.error('Error in uploadToGoogleDrive:', err);
      setError(language === 'en' ? `Upload failed: ${err.message}` : `Upload fehlgeschlagen: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="google-drive-upload-container">
      {error && <p className="upload-error-text">{error}</p>}
      
      <button 
        className="google-drive-upload-button"
        onClick={handleUpload}
        disabled={uploading || !selectedFiles || selectedFiles.length === 0}
      >
        {uploading ? (
          language === 'en' ? 'Uploading...' : 'Wird hochgeladen...'
        ) : (
          language === 'en' ? 'Upload to Google Drive' : 'Zu Google Drive hochladen'
        )}
      </button>
    </div>
  );
}

export default GoogleDriveUpload; 